import { useState } from "react";
import type { KeyboardEvent } from "react";
import { apiPost, ApiError } from "../api/client";
import { humanizeConfigPath, RADIO_CONTROL_PARAM_SNAPSHOT_PATH } from '../lib/definitions/settings';
import { MESHTASTIC_CHANNEL_FIELDS, MESHTASTIC_CONFIG_SECTIONS, MESHTASTIC_MODULE_SECTIONS } from "../lib/meshtasticSchema.generated";
import type { MeshtasticField } from "../lib/meshtasticSchema.generated";
import { MeshtasticValuesForm } from "./MeshtasticValuesForm";
import type { MeshtasticSchemaKind } from "./MeshtasticValuesForm";
import type { RadioConfigEntry, RadioControlExecuteResponse, RadioControlOperation, RadioControlParamSpec } from "../api/types";

// One operation, rendered ENTIRELY from what the capabilities endpoint
// reports (RADIO_CONTROL_CONTRACT.md section 2: "nothing anywhere else may
// hardcode an operation id, a label, or an RF class — the frontend reads
// them from the API"). No operation id, label, summary or rf_note is ever
// written here — a new backend operation must show up with zero frontend
// changes.
//
// Flow (2026-09-23 redesign): a card shows ONE primary action, "Apply",
// which runs POST .../execute with dry_run:false and confirm:<operation
// id> directly. A card with at least one operator-editable param keeps
// Apply disabled until the operator has actually touched one of those
// params; a card with no editable params (reboot, factory reset, send
// advert — an action, not a form) is always enabled. The diff this
// endpoint returns in `changed` is shown AFTER a successful apply, under
// an "Applied" heading, rather than gating the write behind a separate
// preview step. Destructive operations still require the operator to
// type the operation id, rendered inline on the card whenever it's
// expanded, before Apply enables.

// A leaf value equal to the backend's literal redaction placeholder is
// treated as "unchanged" and stripped out (never sent), at any depth,
// regardless of whether the operator's form marks that field "touched" —
// this is what stops a value loaded from GET /api/radio/config (which
// redacts secrets to this exact string) from ever round-tripping back as a
// literal secret value. See RADIO_CONTROL_CONTRACT.md section 7. The
// backend also rejects "<redacted>" with a 422 as a backstop, but this must
// not rely on that.
const REDACTED_PLACEHOLDER = "<redacted>";
const OMIT = Symbol("omit-redacted");

function cleanRedacted(v: unknown): unknown {
  if (v === REDACTED_PLACEHOLDER) return OMIT;
  if (Array.isArray(v)) {
    return v.map(cleanRedacted).filter((x) => x !== OMIT);
  }
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, vv] of Object.entries(v as Record<string, unknown>)) {
      const c = cleanRedacted(vv);
      if (c !== OMIT) out[k] = c;
    }
    return out;
  }
  return v;
}

interface FieldState {
  // Only a touched field is ever included in the outgoing params object —
  // an untouched optional field means "leave unchanged" (contract section
  // 7: "a blank/absent value means leave unchanged"), never sent as "" or
  // false. `value` holds the raw JSON text for an "object"-typed param
  // while it's being edited.
  touched: boolean;
  value: unknown;
}

function blankField(): FieldState {
  return { touched: false, value: "" };
}

function fmtDiffValue(v: unknown): string {
  if (v === null || v === undefined) return "(none)";
  if (typeof v === "object") return JSON.stringify(v);
  return String(v);
}

// Best-effort convenience only, not a contract-confirmed shape: RadioConfigEntry.device
// is documented as an opaque, protocol-shaped tree (protobuf-nested for
// Meshtastic) with no pinned schema. When an operation exposes both a
// choice-style key param (e.g. set_config's "section", set_module_config's
// "module") and an object-typed "values" param, this makes a best-effort
// guess at the corresponding live subtree from the read-view snapshot so
// the operator isn't starting from a blank object — it is never assumed to
// be a perfect or complete match, and a miss just yields an empty seed.
function seedObjectValue(entries: RadioConfigEntry[] | null, transportId: string, keyValue: string): Record<string, unknown> | undefined {
  if (!entries || !keyValue) return undefined;
  const entry = entries.find((e) => e.transport_id === transportId);
  const device = entry?.device as Record<string, unknown> | undefined;
  if (!device) return undefined;
  const candidates: unknown[] = [
    (device.config as Record<string, unknown> | undefined)?.[keyValue],
    (device.module_config as Record<string, unknown> | undefined)?.[keyValue],
    device[keyValue],
  ];
  const found = candidates.find((c) => c && typeof c === "object" && !Array.isArray(c));
  return found as Record<string, unknown> | undefined;
}

// Same idea as seedObjectValue above but for set_channel: channels live in
// RadioConfigEntry.channels, a fixed-position array (Meshtastic always
// reports exactly 8 slots), not a named-key lookup under device.config —
// so the "current value" for channel index N is simply that array's Nth
// entry, however few or many entries this snapshot actually captured.
function seedChannelValue(entries: RadioConfigEntry[] | null, transportId: string, indexValue: string): Record<string, unknown> | undefined {
  if (!entries || indexValue === "") return undefined;
  const idx = Number(indexValue);
  if (!Number.isInteger(idx) || idx < 0) return undefined;
  const entry = entries.find((e) => e.transport_id === transportId);
  const channels = Array.isArray(entry?.channels) ? entry!.channels : undefined;
  const found = channels?.[idx];
  return found && typeof found === "object" && !Array.isArray(found) ? (found as Record<string, unknown>) : undefined;
}

// Seeds a plain ParamField from the live snapshot, via
// RADIO_CONTROL_PARAM_SNAPSHOT_PATH (definitions.ts) — that map is keyed on
// PARAM NAME, not operation id (see its own header comment), so this walks a
// dotted path ("device.radio_freq") off the matching transport's
// RadioConfigEntry. Returns undefined whenever any segment along the way is
// missing/null — including "no map entry for this param at all" — which
// ParamField and buildParams both treat as "no seed," never a zero or a
// placeholder standing in for a real value.
function seedParamValue(entries: RadioConfigEntry[] | null, transportId: string, paramName: string): unknown {
  const path = RADIO_CONTROL_PARAM_SNAPSHOT_PATH[paramName];
  if (!entries || !path) return undefined;
  const entry = entries.find((e) => e.transport_id === transportId);
  if (!entry) return undefined;
  let cur: unknown = entry;
  for (const segment of path.split(".")) {
    if (cur === null || typeof cur !== "object") return undefined;
    cur = (cur as Record<string, unknown>)[segment];
  }
  return cur === null ? undefined : cur;
}

// Detects the three known object-shaped ("values") params by PARAM SHAPE —
// an object param named "values" alongside a sibling "section"/"module"
// choice param, or alongside an "index" param with neither — never by
// checking op.id, per this file's own contract comment at the top ("no
// operation id ... hardcoded here"). A future operation with a "values"
// object param shaped like one of these three is picked up automatically;
// one shaped differently falls through to ParamField's honest "no typed
// form available" notice rather than ever offering raw JSON entry.
function detectMeshtasticValuesShape(opParams: RadioControlParamSpec[]): {
  valuesParam: RadioControlParamSpec;
  schemaKind: MeshtasticSchemaKind;
  keyParam?: RadioControlParamSpec;
} | null {
  const valuesParam = opParams.find((p) => p.name === "values" && p.type === "object");
  if (!valuesParam) return null;
  const sectionParam = opParams.find((p) => p.name === "section" && (p.choices?.length ?? 0) > 0);
  if (sectionParam) return { valuesParam, schemaKind: "config", keyParam: sectionParam };
  const moduleParam = opParams.find((p) => p.name === "module" && (p.choices?.length ?? 0) > 0);
  if (moduleParam) return { valuesParam, schemaKind: "module", keyParam: moduleParam };
  const indexParam = opParams.find((p) => p.name === "index");
  if (indexParam) return { valuesParam, schemaKind: "channel", keyParam: indexParam };
  return null;
}

// Channel writes take their field list from Channel + ChannelSettings
// flattened together (see meshtasticSchema.generated.ts's header): Channel
// contributes index, settings (a nested ChannelSettings message), role;
// ChannelSettings contributes channel_num, psk, name, id, uplink_enabled,
// downlink_enabled, module_settings (another nested message). Three of
// those are excluded rather than rendered as an inert "not editable"
// notice: "index" is already supplied by set_channel's separate required
// "Index" param, never part of "values"; "settings" is Channel's wrapper
// around exactly the ChannelSettings fields already flattened in directly
// alongside it, so showing it too would just be a redundant placeholder for
// data already editable above it; "module_settings" is a nested message
// whose real content (e.g. positionPrecision) isn't in this schema at all
// yet. None of the three loses real editable data — nothing else here
// represents "index"/"role" differently, and the other two have no
// separate representation to lose.
const CHANNEL_VALUES_FIELDS: readonly MeshtasticField[] = MESHTASTIC_CHANNEL_FIELDS.filter(
  (f) => f.name !== "index" && f.name !== "settings" && f.name !== "module_settings",
);

function ParamField({
  spec,
  state,
  currentValue,
  onChange,
  error,
}: {
  spec: RadioControlParamSpec;
  state: FieldState;
  // The live snapshot value for this param (RADIO_CONTROL_PARAM_SNAPSHOT_PATH
  // / seedParamValue, above), if this app has one — undefined when it
  // doesn't map a seed for this param, or the transport's snapshot doesn't
  // have one right now.
  currentValue?: unknown;
  onChange: (next: FieldState) => void;
  error?: string | null;
}) {
  const label = `${humanizeConfigPath(spec.name)}${spec.required ? " (required)" : " (optional)"}`;

  // Mirrors MeshtasticValuesForm's FieldControl exactly (see that
  // component's own header comment): an untouched field displays the live
  // snapshot value rather than starting blank, so the operator sees the
  // device's real current state before typing anything. A seed equal to the
  // backend's literal redaction placeholder is never displayed as text —
  // same "unchanged unless you type a new value" placeholder treatment — and
  // cleanRedacted() (this file, above) is the backstop that keeps it from
  // ever being submitted even if it somehow were.
  const isRedacted = currentValue === REDACTED_PLACEHOLDER;
  const displayValue = state.touched ? state.value : isRedacted ? undefined : currentValue;

  if (spec.type === "bool") {
    return (
      <label className="settings-field settings-field-span-full" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
        <input
          type="checkbox"
          checked={displayValue === true}
          onChange={(e) => onChange({ touched: true, value: e.target.checked })}
        />
        <span>{label}</span>
        {spec.description && <span className="settings-field-desc">{spec.description}</span>}
      </label>
    );
  }

  if (spec.type === "object") {
    // No raw-JSON entry point anywhere, structurally: the three known
    // object-shaped params (set_config/set_module_config/set_channel's
    // "values") are matched by shape in RadioControlOperationCard and
    // rendered as MeshtasticValuesForm before this branch is ever reached.
    // This exists only as a defined, honest failure mode should some future
    // operation add an object param this app doesn't yet have a typed
    // schema for — never a textarea to type structured data into by hand.
    return (
      <div className="settings-field settings-field-span-full">
        <span>{label}</span>
        <div className="radio-op-blocked">
          <strong>No typed form available for this field.</strong>
          <p>
            This operation expects structured data this app has no generated field schema for yet. This is a gap to
            fix, not something to work around by typing JSON — it is not offered here.
          </p>
        </div>
        {spec.description && <span className="settings-field-desc">{spec.description}</span>}
        {error && <span className="radio-op-field-error">{error}</span>}
      </div>
    );
  }

  if (spec.choices && spec.choices.length > 0) {
    return (
      <label className="settings-field">
        <span>{label}</span>
        <select
          value={typeof displayValue === "string" ? displayValue : ""}
          onChange={(e) => onChange({ touched: true, value: e.target.value })}
        >
          <option value="">(choose…)</option>
          {spec.choices.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
        {spec.description && <span className="settings-field-desc">{spec.description}</span>}
      </label>
    );
  }

  if (spec.type === "int" || spec.type === "float") {
    return (
      <label className="settings-field">
        <span>{label}</span>
        <input
          type="number"
          step={spec.type === "float" ? "any" : 1}
          value={typeof displayValue === "number" ? displayValue : ""}
          onChange={(e) => onChange({ touched: true, value: e.target.value === "" ? "" : Number(e.target.value) })}
        />
        {spec.description && <span className="settings-field-desc">{spec.description}</span>}
        {error && <span className="radio-op-field-error">{error}</span>}
      </label>
    );
  }

  // "str" and "node_id" both render as plain text — a node_id is carried as
  // whatever string/number identifier the operator has on hand, never a
  // picker (the frontend has no reliable cross-protocol node directory
  // lookup wired into this form).
  return (
    <label className="settings-field">
      <span>{label}</span>
      <input
        type="text"
        value={typeof displayValue === "string" ? displayValue : ""}
        placeholder={isRedacted ? "Unchanged unless you type a new value" : undefined}
        onChange={(e) => onChange({ touched: true, value: e.target.value })}
      />
      {spec.description && <span className="settings-field-desc">{spec.description}</span>}
      {error && <span className="radio-op-field-error">{error}</span>}
    </label>
  );
}

type Phase = "idle" | "applying" | "applied";

const RF_CLASS_BADGE: Record<string, string> = {
  local: "Local — no emission",
  local_rf_side_effect: "Local — may trigger a broadcast",
  rf: "Radio-frequency",
};

export function RadioControlOperationCard({
  transportId,
  op,
  entries,
  fixedParams,
  titleOverride,
  anchorId,
  hideOwnCopy,
  expanded,
  onToggle,
}: {
  transportId: string;
  op: RadioControlOperation;
  entries: RadioConfigEntry[] | null;
  // Param values pre-bound by the caller (RadioControlPanel, expanding a
  // choice-shaped write operation into one card per choice — see its own
  // header comment). A param named here is never rendered as a form
  // control: its value is fixed by the caller, always satisfies that
  // param's `required` check, and flows into the outgoing params object
  // exactly like a touched field would, including the `cleanRedacted()`
  // strip.
  fixedParams?: Record<string, unknown>;
  // Rendered in `.radio-op-header`'s `.radio-op-label` in place of op.label
  // when present — e.g. "LoRa" instead of the raw operation label, once an
  // operation has been expanded into one card per section/channel.
  titleOverride?: string;
  // Put on the root `.radio-op-card` div as `id`, so a section-nav chip
  // (RadioConfigSection.tsx) can scroll this exact card into view.
  anchorId?: string;
  // True only when the CALLER has already rendered this operation's
  // summary/rf_note once, above MULTIPLE cards this one operation
  // produced (e.g. RadioControlPanel expanding set_config into one card
  // per Meshtastic config section). This is about card COUNT, not about
  // whether fixedParams is present — NodeCommandsSection also passes
  // fixedParams (to pre-bind the viewed node as the operation's subject)
  // but produces exactly one card per operation, so it never sets this
  // and the card shows its own copy, same as any plain unexpanded card.
  // Defaults to false: every card shows its own summary/rf_note unless a
  // caller explicitly says a group-level copy already covers it.
  hideOwnCopy?: boolean;
  // Collapse state and toggle — owned by the caller (RadioControlPanel's
  // TransportPanel), which also drives the chip row that must expand a
  // card before scrolling to it. Every card starts collapsed. Collapsing
  // hides content via the `hidden` attribute rather than conditionally
  // rendering it away, so this card's own state (typed field values, a
  // shown preview diff, a part-typed destructive confirmation) is never
  // lost by collapsing and re-expanding — only whether it's visible
  // changes, never whether it's mounted.
  expanded: boolean;
  onToggle: () => void;
}) {
  // Defensive: the contract always declares `params: []` at minimum, but
  // this backend is being built concurrently against the same contract —
  // never assume the array is present.
  const opParams = Array.isArray(op.params) ? op.params : [];

  const [fields, setFields] = useState<Record<string, FieldState>>(() => {
    const init: Record<string, FieldState> = {};
    for (const p of opParams) init[p.name] = blankField();
    return init;
  });
  const [phase, setPhase] = useState<Phase>("idle");
  const [applyResult, setApplyResult] = useState<RadioControlExecuteResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [destructiveConfirmText, setDestructiveConfirmText] = useState("");

  function updateField(name: string, next: FieldState) {
    setFields((prev) => ({ ...prev, [name]: next }));
    // Editing after a completed apply starts a fresh edit session — clear
    // the stale result rather than let it stand in for a not-yet-applied
    // change.
    if (phase === "applied") {
      setPhase("idle");
      setApplyResult(null);
      setDestructiveConfirmText("");
    }
    setErrorMsg(null);
    setFieldErrors((prev) => {
      if (!(name in prev)) return prev;
      const next2 = { ...prev };
      delete next2[name];
      return next2;
    });
  }

  // Builds the outgoing params object, validates required fields and
  // JSON/number parsing, then strips any "<redacted>" leaf before it ever
  // leaves the browser.
  //
  // WHY THIS ISN'T "touched fields only" (unlike MeshtasticValuesForm, whose
  // own header comment explains why touched-only IS correct there): that
  // Meshtastic write path merges the submitted dict onto a fresh COPY of the
  // device's real live protobuf, so an omitted field is safe — it just keeps
  // its true current value. MeshCore's set_radio has no equivalent fresh
  // merge base. frequency/bandwidth/spreading_factor/coding_rate are all
  // declared optional, and the backend (meshcore_tcp.py:687) fills any
  // omitted one from the device's CACHED self_info — populated once from the
  // SELF_INFO event at connect time and never refreshed after a write. Edit
  // frequency and apply, then edit bandwidth and apply: the second write's
  // omitted frequency gets merged back in from that stale pre-first-write
  // cache, silently reverting the first change. There's also no
  // per-parameter MeshCore wire write to fall back on regardless — the
  // library always builds one frame carrying all four fields every time. So
  // the rule here is: once the operator has touched ANYTHING on this card,
  // every OTHER param this app has a live snapshot seed for (see
  // seedParamValue/RADIO_CONTROL_PARAM_SNAPSHOT_PATH above) rides along at
  // that seeded value rather than being omitted and left to the backend's
  // stale cache. A param with no seed is unaffected either way — still
  // omitted, same as on an untouched card. Someone will be tempted to "fix"
  // this back to touched-only to match MeshtasticValuesForm — don't; the
  // merge-safety property that makes touched-only correct there does not
  // hold here.
  function buildParams(): { ok: true; params: Record<string, unknown> } | { ok: false; fieldName?: string; message: string } {
    const params: Record<string, unknown> = {};
    const anyTouched = opParams.some((spec) => {
      if (fixedParams && spec.name in fixedParams) return false;
      return (fields[spec.name] ?? blankField()).touched;
    });
    for (const spec of opParams) {
      // A fixed param is never read from `fields` — it's supplied by the
      // caller and always counts as present, so it can never trigger this
      // spec's "X is required" check below.
      if (fixedParams && spec.name in fixedParams) {
        params[spec.name] = fixedParams[spec.name];
        continue;
      }
      let fs = fields[spec.name] ?? blankField();
      if (!fs.touched && anyTouched) {
        // The operator touched some other param on this card — pull this
        // one's current snapshot value along too, per the rule above. A
        // "<redacted>" seed is deliberately excluded here (not just left to
        // cleanRedacted() below): it can never be a real value to write, and
        // for a non-string param type letting it through would fail this
        // loop's own type coercion below rather than being cleanly omitted.
        const seed = seedParamValue(entries, transportId, spec.name);
        if (seed !== undefined && seed !== null && seed !== REDACTED_PLACEHOLDER) {
          fs = { touched: true, value: seed };
        }
      }
      if (!fs.touched) {
        if (spec.required) return { ok: false, fieldName: spec.name, message: `${humanizeConfigPath(spec.name)} is required.` };
        continue;
      }
      if (spec.type === "object") {
        // MeshtasticValuesForm reports an already-structured object (only
        // its touched sub-fields) — never JSON text. The string/JSON.parse
        // path below only remains for ParamField's own now-textarea-less
        // "object" branch, which never produces a string value either; it
        // stays purely so a value genuinely of the wrong shape is reported
        // as a clear error rather than silently passed through.
        if (fs.value && typeof fs.value === "object" && !Array.isArray(fs.value)) {
          params[spec.name] = fs.value;
          continue;
        }
        const text = typeof fs.value === "string" ? fs.value.trim() : "";
        if (text === "") {
          if (spec.required) return { ok: false, fieldName: spec.name, message: `${humanizeConfigPath(spec.name)} is required.` };
          continue;
        }
        try {
          params[spec.name] = JSON.parse(text);
        } catch (e) {
          return { ok: false, fieldName: spec.name, message: `Invalid JSON: ${(e as Error).message}` };
        }
        continue;
      }
      if (spec.type === "bool") {
        params[spec.name] = fs.value === true;
        continue;
      }
      if (spec.type === "int" || spec.type === "float") {
        if (fs.value === "" || fs.value === undefined) {
          if (spec.required) return { ok: false, fieldName: spec.name, message: `${humanizeConfigPath(spec.name)} is required.` };
          continue;
        }
        const n = Number(fs.value);
        if (Number.isNaN(n)) return { ok: false, fieldName: spec.name, message: `${humanizeConfigPath(spec.name)} must be a number.` };
        params[spec.name] = spec.type === "int" ? Math.trunc(n) : n;
        continue;
      }
      // str / node_id
      const s = typeof fs.value === "string" ? fs.value : "";
      if (s === "") {
        if (spec.required) return { ok: false, fieldName: spec.name, message: `${humanizeConfigPath(spec.name)} is required.` };
        continue;
      }
      params[spec.name] = s;
    }
    return { ok: true, params: cleanRedacted(params) as Record<string, unknown> };
  }

  function describeError(e: unknown): string {
    if (e instanceof ApiError) {
      if (e.status === 409) return `Refused by the transmit guard: ${e.message}`;
      if (e.status === 503) return `Transport not connected: ${e.message}`;
      if (e.status === 501) return `Not supported by the installed library: ${e.message}`;
      if (e.status === 422) return `Rejected: ${e.message}`;
      if (e.status === 400) return `Confirmation required: ${e.message}`;
      return e.message;
    }
    return e instanceof Error ? e.message : "Request failed.";
  }

  async function runExecute(dryRun: boolean): Promise<RadioControlExecuteResponse | null> {
    const built = buildParams();
    if (!built.ok) {
      if (built.fieldName) setFieldErrors({ [built.fieldName]: built.message });
      else setErrorMsg(built.message);
      return null;
    }
    setErrorMsg(null);
    setFieldErrors({});
    try {
      return await apiPost<RadioControlExecuteResponse>("/api/radio/control/execute", {
        transport_id: transportId,
        operation: op.id,
        params: built.params,
        confirm: op.id,
        dry_run: dryRun,
      });
    } catch (e) {
      setErrorMsg(describeError(e));
      return null;
    }
  }

  async function onApply() {
    setPhase("applying");
    const res = await runExecute(false);
    if (res) {
      setApplyResult(res);
      setPhase("applied");
    } else {
      setPhase("idle");
    }
  }

  // Replaces set_config/set_module_config/set_channel's raw-JSON "values"
  // param with a real typed form (task brief: "Replace every JSON textarea
  // with real form controls") — detected by param shape, not op.id, so
  // ParamField's plain params render unchanged for every other operation.
  const meshtasticShape = detectMeshtasticValuesShape(opParams);
  // A fixed key param (RadioControlPanel expanding set_config/
  // set_module_config/set_channel into one card per section/channel) wins
  // over `fields` — there's nothing in `fields` for it, since it's filtered
  // out of the rendered form below. This is what lets the schema and the
  // live-snapshot seed resolve without the operator choosing anything.
  const chosenKeyValue = meshtasticShape?.keyParam
    ? fixedParams && meshtasticShape.keyParam.name in fixedParams
      ? fixedParams[meshtasticShape.keyParam.name]
      : fields[meshtasticShape.keyParam.name]?.value
    : undefined;
  const chosenKeyString =
    typeof chosenKeyValue === "string" ? chosenKeyValue : typeof chosenKeyValue === "number" ? String(chosenKeyValue) : "";

  let meshtasticSchemaFields: readonly MeshtasticField[] | undefined;
  let meshtasticCurrentSection: Record<string, unknown> | undefined;
  if (meshtasticShape?.schemaKind === "config" && chosenKeyString) {
    meshtasticSchemaFields = MESHTASTIC_CONFIG_SECTIONS[chosenKeyString];
    meshtasticCurrentSection = seedObjectValue(entries, transportId, chosenKeyString);
  } else if (meshtasticShape?.schemaKind === "module" && chosenKeyString) {
    meshtasticSchemaFields = MESHTASTIC_MODULE_SECTIONS[chosenKeyString];
    meshtasticCurrentSection = seedObjectValue(entries, transportId, chosenKeyString);
  } else if (meshtasticShape?.schemaKind === "channel") {
    meshtasticSchemaFields = CHANNEL_VALUES_FIELDS;
    meshtasticCurrentSection = seedChannelValue(entries, transportId, chosenKeyString);
  }

  const destructiveConfirmed = !op.destructive || destructiveConfirmText === op.id;

  // Apply's enable rule (Change 1): a card with at least one
  // operator-editable param — i.e. one not supplied by `fixedParams` — stays
  // disabled until the operator has actually touched one of them; a seeded
  // snapshot value showing in the field is not a touch (see ParamField's and
  // MeshtasticValuesForm's own "touched" tracking above). A card with no
  // editable params at all (op.params is empty, or every param is fixed by
  // the caller) has nothing to change, so it's always ready.
  const editableParams = opParams.filter((spec) => !(fixedParams && spec.name in fixedParams));
  const hasEditableParams = editableParams.length > 0;
  const anyEdited = editableParams.some((spec) => (fields[spec.name] ?? blankField()).touched);
  const changeReady = !hasEditableParams || anyEdited;

  function onHeaderKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onToggle();
    }
  }

  return (
    <div className="radio-op-card" data-rf-class={op.rf_class} id={anchorId}>
      <div
        className="radio-op-header"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={onToggle}
        onKeyDown={onHeaderKeyDown}
      >
        <span className="radio-op-label">{titleOverride ?? op.label}</span>
        <span className="badge radio-op-rfclass-badge" data-rf-class={op.rf_class}>
          {RF_CLASS_BADGE[op.rf_class] ?? op.rf_class}
        </span>
        {op.destructive && (
          <span className="badge radio-op-destructive-badge">Destructive</span>
        )}
        {op.restart_required && <span className="settings-restart-badge">Needs a restart to take effect</span>}
        <span className="radio-op-toggle-icon" aria-hidden="true">
          {expanded ? "▲" : "▼"}
        </span>
      </div>

      {/* Collapsed by default — hidden via the `hidden` attribute (CSS),
          never unmounted, so a card's own in-progress state (typed fields,
          a shown preview, a part-typed destructive confirm) survives a
          collapse/re-expand exactly as left. The header above always keeps
          the title, rf-class badge, Destructive badge and restart-required
          badge visible regardless of collapse state — that's the RF safety
          boundary and it must never be the thing that gets hidden. */}
      <div className="radio-op-collapsible" hidden={!expanded}>
      {/* On a card whose operation produced MULTIPLE cards (hideOwnCopy —
          set by the caller only in that case, e.g. RadioControlPanel
          expanding set_config into one card per Meshtastic section), the
          group renders this operation's summary and rf_note exactly once
          above all of them instead — see RadioControlPanel.tsx's
          TransportPanel. Every other card (one card per operation, whether
          or not it has fixedParams — NodeCommandsSection pre-binds the
          viewed node this way too) renders both here, inside the
          collapsible wrapper, so a collapsed card is just its header row
          and both lines appear the moment it's expanded — before anyone
          can act on it. */}
      {!hideOwnCopy && op.summary && <p className="settings-field-desc">{op.summary}</p>}
      {!hideOwnCopy && op.rf_note && (
        <p className="radio-op-rf-note" data-rf-class={op.rf_class}>
          {op.rf_note}
        </p>
      )}

      {!op.supported && (
        <div className="radio-op-blocked">
          <strong>Not supported by the installed library.</strong>
          <p>{op.blocked_reason || "This operation cannot run with the currently installed library."}</p>
        </div>
      )}

      {op.supported && !op.available && (
        <div className="radio-op-blocked">
          <strong>Currently blocked.</strong>
          <p>{op.blocked_reason || "This control is not available right now."}</p>
        </div>
      )}

      {op.supported && op.available && (
        <div className="radio-op-body">
          {(() => {
            const visibleParams = opParams
              .filter((spec) => spec !== meshtasticShape?.valuesParam)
              .filter((spec) => !(fixedParams && spec.name in fixedParams));
            return (
              visibleParams.length > 0 && (
                <div className="settings-fields-grid">
                  {visibleParams.map((spec) => (
                    <ParamField
                      key={spec.name}
                      spec={spec}
                      state={fields[spec.name] ?? blankField()}
                      currentValue={seedParamValue(entries, transportId, spec.name)}
                      onChange={(next) => updateField(spec.name, next)}
                      error={fieldErrors[spec.name]}
                    />
                  ))}
                </div>
              )
            );
          })()}

          {meshtasticShape &&
            (meshtasticSchemaFields ? (
              <MeshtasticValuesForm
                // Force a clean remount on every section/module/index
                // switch — this form keeps its own per-field draft state,
                // which must never survive into a different section's
                // differently-shaped fields.
                key={`${meshtasticShape.schemaKind}:${chosenKeyString}`}
                schemaKind={meshtasticShape.schemaKind}
                // The card header already names the section via
                // titleOverride on an expanded card — suppress this form's
                // own duplicate heading in that case (Fix 2).
                sectionName={!titleOverride && meshtasticShape.schemaKind !== "channel" ? chosenKeyString : undefined}
                fields={meshtasticSchemaFields}
                currentSection={meshtasticCurrentSection}
                hasLiveSnapshot={!!meshtasticCurrentSection}
                onChange={(values) => updateField(meshtasticShape.valuesParam.name, { touched: true, value: values })}
              />
            ) : (
              <p className="settings-field-desc">
                Choose {meshtasticShape.schemaKind === "config" ? "a section" : meshtasticShape.schemaKind === "module" ? "a module" : "an index"}{" "}
                above to edit its fields.
              </p>
            ))}

          {/* Change 3: inline destructive confirmation, visible on the card
              body whenever it's expanded — no longer gated behind a preview
              step. */}
          {op.destructive && phase !== "applied" && (
            <label className="settings-field settings-field-span-full">
              <span>
                Type <code>{op.id}</code> to confirm this destructive action
              </span>
              <input type="text" value={destructiveConfirmText} onChange={(e) => setDestructiveConfirmText(e.target.value)} />
            </label>
          )}

          {errorMsg && <div className="radio-op-error">{errorMsg}</div>}

          {phase === "applied" && applyResult && (
            <div className="radio-op-diff" data-kind="applied">
              <h5>Applied</h5>
              {(Array.isArray(applyResult.changed) ? applyResult.changed : []).length === 0 ? (
                <p className="settings-field-desc">No changes detected.</p>
              ) : (
                <table className="radio-op-diff-table">
                  <thead>
                    <tr>
                      <th>Field</th>
                      <th>From</th>
                      <th>To</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(Array.isArray(applyResult.changed) ? applyResult.changed : []).map((c) => (
                      <tr key={c.path}>
                        <td>{c.path}</td>
                        <td>{fmtDiffValue(c.previous)}</td>
                        <td>{fmtDiffValue(c.new)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="settings-save-status" data-kind="ok">
                {applyResult.detail}
              </p>
              {applyResult.restart_required && (
                <p className="radio-op-restart-note">
                  {applyResult.restart_notice || "The device needs to be rebooted before this change takes effect — it is not live yet."}
                </p>
              )}
            </div>
          )}

          <div className="radio-op-actions">
            {phase !== "applied" && (
              <button
                type="button"
                className="settings-save-btn radio-op-apply-btn"
                disabled={phase === "applying" || !changeReady || !destructiveConfirmed}
                onClick={onApply}
              >
                {phase === "applying" ? "Applying…" : "Apply"}
              </button>
            )}
            {phase === "applied" && (
              <button
                type="button"
                className="settings-save-btn"
                onClick={() => {
                  setPhase("idle");
                  setApplyResult(null);
                  setDestructiveConfirmText("");
                  const reset: Record<string, FieldState> = {};
                  for (const p of opParams) reset[p.name] = blankField();
                  setFields(reset);
                }}
              >
                Run again
              </button>
            )}
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
